import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { BOOKING_POLICY, BRAND } from "@/data/content";
import { track } from "@/lib/analytics";

const EASE = [0.16, 1, 0.3, 1];

export default function PolicyPage() {
  useEffect(() => {
    document.title = "Booking & Cancellation Policy | Rock Raven";
    track("pageview");
  }, []);

  return (
    <div data-testid="policy-page" className="min-h-screen bg-coal text-amber-50">
      <header className="border-b border-amber-500/10 bg-[#0A0908]/90 backdrop-blur-xl">
        <nav className="flex items-center justify-between px-6 py-4 sm:px-10 lg:px-16">
          <a href="/" data-testid="policy-brand-link" className="flex items-center gap-2.5">
            <img src={BRAND.emblem} alt="Rock Raven emblem" className="h-14 w-auto" />
            <span className="font-serif text-xl uppercase tracking-[0.2em] text-amber-50 sm:text-2xl">
              {BRAND.name}
            </span>
          </a>
          <a
            href="/"
            data-testid="policy-back-button"
            className="group inline-flex items-center gap-2 border border-amber-500/25 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-amber-200 transition-colors duration-300 hover:border-amber-400 hover:text-amber-50"
          >
            <ArrowLeft className="size-3.5 transition-transform duration-300 group-hover:-translate-x-1" />
            Back to Site
          </a>
        </nav>
      </header>

      <main className="px-6 py-20 sm:px-10 lg:px-16 lg:py-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mx-auto max-w-3xl"
        >
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.35em] text-gold">
            {BOOKING_POLICY.eyebrow}
          </p>
          <h1 className="bg-gradient-to-r from-amber-100 via-gold to-ember bg-clip-text font-serif text-4xl uppercase tracking-tight text-transparent sm:text-5xl">
            {BOOKING_POLICY.heading}
          </h1>
          <p className="mt-6 text-base font-light leading-relaxed text-neutral-300 sm:text-lg">
            {BOOKING_POLICY.intro}
          </p>
        </motion.div>

        <div className="mx-auto mt-12 grid max-w-6xl gap-4 md:grid-cols-3">
          {BOOKING_POLICY.items.map((item, i) => (
            <motion.div
              key={item.title}
              data-testid={`policy-page-item-${i + 1}`}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 + i * 0.08, ease: EASE }}
              className="border border-amber-500/15 bg-[#141210]/90 p-7"
            >
              <h2 className="font-serif text-xl text-amber-100">{item.title}</h2>
              <p className="mt-4 text-sm font-light leading-relaxed text-neutral-400">
                {item.text}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35, ease: EASE }}
          className="mx-auto mt-10 max-w-3xl"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            {BOOKING_POLICY.note}
          </p>
          <a
            href="/#contact"
            data-testid="policy-contact-cta"
            onClick={() => track("cta_click", { location: "policy_page" })}
            className="group mt-8 inline-flex items-center gap-3 border border-amber-500/30 px-7 py-4 font-mono text-xs uppercase tracking-[0.25em] text-amber-200 transition-all duration-300 hover:border-amber-400 hover:text-amber-50 hover:shadow-[0_0_40px_rgba(230,81,0,0.2)]"
          >
            Ask About Your Date
            <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </motion.div>
      </main>
    </div>
  );
}
